import { FormControl } from '@angular/forms'
import { GeoJsonWagonPositionProps } from '@geo-util-lib/geo.model'
import { DropdownOption } from '@shared-ui-lib/dropdown/dropdown.model'
import { DetailSegment, Segment } from '@wagon-lib/segments.model'
import { WagonMovementState, WagonType } from '@wagon-lib/wagon.model'
import { FeatureCollection, Geometry } from 'geojson'
import { GeoJSON, Polygon } from 'leaflet'

export interface WagonTrackingMovementCounts {
    idle: number
    moving: number
    inactive: number
}

/* Markers & Clusters */
export type WagonTrackingMarkerManagerItem = GeoJSON<GeoJsonWagonPositionProps>

export type WagonTrackingMarkerManagerList = {
    [id: string]: WagonTrackingMarkerManagerItem
}

/* Filters */
export interface WagonTrackingFilter<T> {
    control: FormControl<T[] | null>
    label: string
    options: DropdownOption<T>[]
}

export type WagonTrackingFilters = {
    wagonType: WagonTrackingFilter<WagonType>
    state: WagonTrackingFilter<WagonMovementState>
    uicClass: WagonTrackingFilter<string>
}

export type WagonTrackingFilterValues = Partial<{
    wagonType: WagonType[] | null
    state: WagonMovementState[] | null
    uicClass: string[] | null
}>

export type WagonTrackingFilterCounts = {
    wagonType: Map<WagonType, number>
    state: Map<WagonMovementState, number>
    uicClass: Map<string, number>
}

/* Geofences */
export enum WagonTrackingGeofenceOperationType {
    cancel = 'cancel',
    create = 'create',
    selectPrivate = 'selectPrivate',
    selectPublic = 'selectPublic',
    setCustomName = 'setCustomName',
    setShape = 'setShape', // payload is a string encoded from lat-lng list
}

export type WagonTrackingGeofenceOperation = {
    op: WagonTrackingGeofenceOperationType
    payload?: string
}

export enum WagonTrackingGeofenceVisibility {
    PRIVATE = 'Private',
    PUBLIC = 'Public',
    PUBLIC_WITH_SUBSCRIPTION = 'PublicWithSubscription',
}

export const WagonTrackingGeofenceTriggerMap = {
    BOTH: ['ENTER', 'EXIT'],
    ENTER: ['ENTER'],
    EXIT: ['EXIT'],
} as const

export type WagonTrackingGeofenceTriggerKey =
    keyof typeof WagonTrackingGeofenceTriggerMap

export type WagonTrackingGeofenceTrigger =
    (typeof WagonTrackingGeofenceTriggerMap)[WagonTrackingGeofenceTriggerKey]

export type WagonTrackingGeofence = {
    id: string
    name: string
    shape: string
    visibility: WagonTrackingGeofenceVisibility
    subscriptionTriggers?: WagonTrackingGeofenceTrigger
}

export type WagonTrackingGeofenceWagons = {
    assetIds: string[]
}

export type WagonTrackingGetGeofencesResponse = {
    geofences: WagonTrackingGeofence[]
}

export type WagonTrackingPublicGeofenceRequestParams = {
    name: string
    subscriptionTriggers: WagonTrackingGeofenceTrigger
}

export type WagonTrackingPrivateGeofenceRequestParams = {
    id?: string
    name: string
    subscriptionTriggers: WagonTrackingGeofenceTrigger
    shape: string
}

export type WagonTrackingGeofenceFeatureProps = {
    geofence: WagonTrackingGeofence
}

export type WagonTrackingGeofenceFeatureCollection = FeatureCollection<
    Geometry,
    WagonTrackingGeofenceFeatureProps
>

export enum WagonTrackingGeofenceEvent {
    deselect = 'deselect',
    select = 'select',
}

export enum WagonTrackingGeofenceSelectionOrigin {
    LIST = 'LIST',
    URL = 'URL',
    OTHER = 'OTHER',
}

export type WagonTrackingGeofenceTemplateUtilProps =
    WagonTrackingGeofenceFeatureProps & {
        // enables public geofence utils to refer back to geofence layer
        // (e.g. for event binding and interactions)
        geofenceLayer: Polygon<WagonTrackingGeofenceFeatureProps>
    }

export type WagonTrackingEvent = {
    wagonId: string
    wagonNumber: string
    occurredAt: string
    geofenceName: string
    eventType: WagonTrackingEventType
    segment: Segment
    detailSegment: DetailSegment
}

export type WagonTrackingEventRequestParams = {
    periodFrom?: string
    periodTo?: string
    pageNumber: number
    pageSize: number
}

export enum WagonTrackingEventType {
    Enter = 'Enter',
    Exit = 'Exit',
    Inside = 'Inside',
    Cross = 'Cross',
}

/* Journey */
export interface WagonJourneyRequestParams {
    assetId: string
    beginOn?: string
    endOn?: string
}

export interface WagonJourneyResponse {
    assetId: string
    beginOn: string
    endOn: string
    events: WagonJourneyEvents
    polylines: string[]
}

export interface WagonJourneyEvents {
    positions: WagonJourneyPositionEvent[]
}

export interface WagonJourneyPositionEvent {
    latitude: number
    longitude: number
    speed: number
    timestamp: string
}

export const GeofenceSearchStorageKey = 'Geofence Search Settings'

export interface GeofenceSearchPopupSettings {
    alreadyOpenedGeofenceSearch: boolean
}
